import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  MenuItem,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  FolderOpen as FolderOpenIcon,
  Folder as FolderIcon
} from '@mui/icons-material';
import { locationsAPI } from '../api/locations';

function Locations() {
  const [locations, setLocations] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    parent_id: null,
    border_color: '#000000',
    fill_color: '#FFFFFF',
    opacity: 20,
    description: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [locs, tree] = await Promise.all([
        locationsAPI.getAll(),
        locationsAPI.getTree()
      ]);
      setLocations(locs);
      setTreeData(tree);
    } catch (err) {
      setError('Ошибка загрузки данных: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleOpenDialog = (item = null, parentId = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        parent_id: item.parent_id,
        border_color: item.border_color || '#000000',
        fill_color: item.fill_color || '#FFFFFF',
        opacity: item.opacity || 20,
        description: item.description || ''
      });
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        parent_id: parentId,
        border_color: '#000000',
        fill_color: '#FFFFFF',
        opacity: 20,
        description: ''
      });
    }
    setOpenDialog(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const payload = {
        name: formData.name,
        parent_id: formData.parent_id ? parseInt(formData.parent_id) : null,
        border_color: formData.border_color,
        fill_color: formData.fill_color,
        opacity: parseInt(formData.opacity),
        description: formData.description || null
      };

      if (editingItem) {
        await locationsAPI.update(editingItem.id, payload);
      } else {
        await locationsAPI.create(payload);
      }

      await loadData();
      handleCloseDialog();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Вы уверены, что хотите удалить локацию "${name}"? Все вложенные локации также будут удалены!`)) {
      return;
    }

    try {
      await locationsAPI.delete(id);
      await loadData();
    } catch (err) {
      setError('Ошибка удаления: ' + (err.response?.data?.detail || err.message));
    }
  };

  const getLocationName = (locationId) => {
    const loc = locations.find(l => l.id === locationId);
    return loc ? loc.name : 'Нет';
  };

  const handleToggle = (event, nodeIds) => {
    setExpanded(nodeIds);
  };

  const renderTree = (nodes) => (
    <TreeItem
      key={nodes.id}
      nodeId={String(nodes.id)}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body1">{nodes.name}</Typography>
            {nodes.description && (
              <Typography variant="caption" color="text.secondary">
                {nodes.description}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDialog(null, nodes.id);
              }}
              color="success"
            >
              <AddIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const fullNode = locations.find(l => l.id === nodes.id);
                handleOpenDialog(fullNode);
              }}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(nodes.id, nodes.name);
              }}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      }
      icon={<FolderIcon color="primary" />}
      expandIcon={<FolderIcon color="action" />}
      collapseIcon={<FolderOpenIcon color="primary" />}
    >
      {Array.isArray(nodes.children) ? nodes.children.map((node) => renderTree(node)) : null}
    </TreeItem>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Локации</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Добавить корневую локацию
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Дерево локаций
              </Typography>
              {treeData.length > 0 ? (
                <TreeView
                  aria-label="location tree"
                  defaultCollapseIcon={<ExpandMoreIcon />}
                  defaultExpandIcon={<ChevronRightIcon />}
                  expanded={expanded}
                  onNodeToggle={handleToggle}
                  sx={{ flexGrow: 1, overflowY: 'auto' }}
                >
                  {treeData.map((node) => renderTree(node))}
                </TreeView>
              ) : (
                <Typography color="text.secondary">
                  Нет локаций. Создайте корневую локацию.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Список всех локаций
              </Typography>
              <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {locations.map((loc) => (
                  <Paper
                    key={loc.id}
                    sx={{
                      p: 1.5,
                      mb: 1,
                      borderLeft: 4,
                      borderColor: loc.border_color,
                      bgcolor: loc.fill_color,
                      opacity: loc.opacity / 100 + 0.7
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {loc.name}
                        </Typography>
                        {loc.parent_id && (
                          <Typography variant="caption" color="text.secondary">
                            Родитель: {getLocationName(loc.parent_id)}
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <IconButton size="small" onClick={() => handleOpenDialog(loc)} color="primary">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(loc.id, loc.name)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Редактировать локацию' : 'Добавить локацию'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Название"
            name="name"
            value={formData.name}
            onChange={handleChange}
            margin="normal"
            required
            helperText="Например: Здание А, Этаж 2, Серверная"
          />

          <TextField
            fullWidth
            select
            label="Родительская локация"
            name="parent_id"
            value={formData.parent_id || ''}
            onChange={handleChange}
            margin="normal"
          >
            <MenuItem value="">Нет (корневая локация)</MenuItem>
            {locations
              .filter((loc) => !editingItem || loc.id !== editingItem.id)
              .map((loc) => (
                <MenuItem key={loc.id} value={loc.id}>
                  {loc.name}
                </MenuItem>
              ))}
          </TextField>

          <TextField
            fullWidth
            label="Описание"
            name="description"
            value={formData.description}
            onChange={handleChange}
            margin="normal"
            multiline
            rows={2}
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Настройки отображения на карте
          </Typography>

          <TextField
            fullWidth
            type="color"
            label="Цвет границы"
            name="border_color"
            value={formData.border_color}
            onChange={handleChange}
            margin="normal"
          />

          <TextField
            fullWidth
            type="color"
            label="Цвет заливки"
            name="fill_color"
            value={formData.fill_color}
            onChange={handleChange}
            margin="normal"
          />

          <TextField
            fullWidth
            type="number"
            label="Прозрачность (%)"
            name="opacity"
            value={formData.opacity}
            onChange={handleChange}
            margin="normal"
            inputProps={{ min: 0, max: 100 }}
            helperText="0 - полностью прозрачная, 100 - непрозрачная"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.name}
          >
            {loading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Locations;
